import React, { useState } from 'react';
import { X, MessageSquare, Bug, Lightbulb, Send, CheckCircle } from 'lucide-react';

const FeedbackModal = ({ isOpen, onClose, user, API_URL }) => {
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const categories = [
    { id: 'bug', label: 'Bug Report', icon: Bug, color: 'text-red-500', bgColor: 'bg-red-50 border-red-200 hover:bg-red-100' },
    { id: 'feature', label: 'Feature Request', icon: Lightbulb, color: 'text-amber-500', bgColor: 'bg-amber-50 border-amber-200 hover:bg-amber-100' },
    { id: 'general', label: 'General Feedback', icon: MessageSquare, color: 'text-blue-500', bgColor: 'bg-blue-50 border-blue-200 hover:bg-blue-100' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!category) {
      setError('Please select a category');
      return;
    }
    
    if (!message.trim()) {
      setError('Please enter your feedback');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category,
          message: message.trim(),
          user_email: user?.email || 'anonymous',
          user_name: user?.name || 'Anonymous User',
          user_plan: user?.plan || 'free',
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
          setCategory('');
          setMessage('');
          onClose();
        }, 2000);
      } else {
        setError(data.error || 'Failed to submit feedback');
      }
    } catch (err) {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setCategory('');
      setMessage('');
      setError('');
      setIsSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Send Feedback</h2>
                <p className="text-sm text-white/80">Help us improve GeniusList</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Success State */}
        {isSuccess ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Thank you!</h3>
            <p className="text-gray-600">Your feedback has been submitted successfully.</p>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                What type of feedback?
              </label>
              <div className="grid grid-cols-3 gap-3">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  const isSelected = category === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setCategory(cat.id)}
                      className={`
                        p-3 rounded-xl border-2 transition-all text-center
                        ${isSelected 
                          ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' 
                          : `border-gray-200 ${cat.bgColor}`
                        }
                      `}
                      data-testid={`feedback-category-${cat.id}`}
                    >
                      <Icon className={`w-5 h-5 mx-auto mb-1 ${isSelected ? 'text-purple-600' : cat.color}`} />
                      <span className={`text-xs font-medium ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                        {cat.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Message Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your feedback
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                placeholder="Tell us what's on your mind..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-gray-900 placeholder-gray-400"
                data-testid="feedback-message-input"
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-400">
                  {message.length}/500 characters
                </span>
                {message.length >= 450 && (
                  <span className="text-xs text-amber-500">
                    {500 - message.length} remaining
                  </span>
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !category || !message.trim()}
              className={`
                w-full py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all
                ${isSubmitting || !category || !message.trim()
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl'
                }
              `}
              data-testid="feedback-submit-button"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Feedback
                </>
              )}
            </button>

            {/* Privacy Note */}
            <p className="text-xs text-gray-400 text-center">
              Your email ({user?.email || 'anonymous'}) will be attached to help us follow up if needed.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
